import { useState } from "react";
import classes from "./Language.module.css";
import CustomSelecter from "../../../components/Input/Autocomplete/Autocomplete";
import { useTranslation } from "../../../I18n/Languages/LanguageContext";
import { getLanguageLevelOptions, getLanguageOptions } from "../../../utils/translatedOptions";

export default function Language({
  languages,
  updateLanguageEntry,
  addLanguageEntry,
  removeLanguageEntry,
}) {
  const { t } = useTranslation();
  const [currentEditingIndex, setCurrentEditingIndex] = useState(0);
  const [savedEntries, setSavedEntries] = useState([]);

  const isEntryValid = (entry) => {
    if (!entry) return false;
    const requiredFields = ["language", "level"];
    return requiredFields.every(
      (field) => entry[field] && entry[field].trim().length > 0
    );
  };

  const handleAddLanguage = () => {
    const currentEntry = languages[currentEditingIndex];

    if (currentEntry && isEntryValid(currentEntry)) {
      setSavedEntries((prev) => {
        if (!prev.includes(currentEditingIndex)) {
          return [...prev, currentEditingIndex];
        }
        return prev;
      });
    }

    addLanguageEntry();

    setCurrentEditingIndex(languages.length);
  };

  const handleEdit = (index) => {
    setCurrentEditingIndex(index);
  };

  const handleDelete = (id) => {
    const deletedIndex = languages.findIndex((entry) => entry.id === id);
  
    setSavedEntries((prev) => prev.filter((index) => index !== deletedIndex));
  
    removeLanguageEntry(id);
  
    const remainingEntries = languages.filter((entry) => entry.id !== id);
  
    if (remainingEntries.length === 0) {
      setCurrentEditingIndex(0);
    } else if (deletedIndex === currentEditingIndex) {
      setCurrentEditingIndex(0);
    } else if (deletedIndex < currentEditingIndex) {
      setCurrentEditingIndex(currentEditingIndex - 1);
    }
  
    setSavedEntries((prev) => prev.map((index) => (index > deletedIndex ? index - 1 : index)));
  };

  const currentEntry = languages[currentEditingIndex] || {};

  const safeUpdateEntry = (field, value) => {
    if (!languages[currentEditingIndex]) {
      console.warn(
        `Attempting to update entry at index ${currentEditingIndex} but it doesn't exist`
      );
      return;
    }
    updateLanguageEntry(currentEditingIndex, field, value);
  };

  return (
    <>
      <div className={classes.experienceHeader}>
        <div className={classes.h3red}>{t("language_proficiency")}</div>
        <div className={`${classes.buttonWrapper} ${classes.desktopOnly}`}>
        <button
          className={classes.applyBtnBottom}
          onClick={handleAddLanguage}
        >
          {t("add_language")}
        </button>
        </div>
      </div>
      <div className={classes.personalForm}>
        <div className={classes.firstGroup}>
          <CustomSelecter
            value={currentEntry?.language || ""}
            options={getLanguageOptions(t)}
            label={t("select")}
            name="select"
            topLabel={t("language")}
            onChange={(value) => safeUpdateEntry("language", value)}
          />
          <CustomSelecter
            value={currentEntry?.level || ""}
            options={getLanguageLevelOptions(t)}
            label={t("select")}
            name="select"
            topLabel={t("level")}
            onChange={(value) => safeUpdateEntry("level", value)}
          />
        </div>
        <div className={`${classes.buttonWrapper} ${classes.mobileOnly}`}>
        <button
          className={classes.applyBtnBottom}
          onClick={handleAddLanguage}
        >
          {t("add_language")}
        </button>
        </div>
      </div>

      {savedEntries.length > 0 && (
        <div className={classes.summarySection}>
          {savedEntries
            .map((entryIndex) => languages[entryIndex])
            .filter(Boolean)
            .map((entry, displayIndex) => {
              const actualIndex = savedEntries[displayIndex];

              return (
                <div
                  key={entry.id}
                  className={`${classes.experienceSummary} ${
                    actualIndex === currentEditingIndex
                      ? classes.activeSummary
                      : ""
                  }`}
                >
                  <div className={classes.summaryContent}>
                    <div className={classes.summaryHeader}>
                      <h4 className={classes.positionTitle}>
                        {entry.language || t("language_not_specified")}
                      </h4>
                      <span className={classes.company}>
                        {entry.level || t("level_not_specified")}
                      </span>
                    </div>
                  </div>

                  <div className={classes.actionButtons}>
                    <button
                      className={classes.editBtn}
                      onClick={() => handleEdit(actualIndex)}
                    >
                      {t("edit")}
                    </button>
                    <button
                      className={classes.deleteBtn}
                      onClick={() => handleDelete(entry.id)}
                    >
                      {t("delete")}
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </>
  );
}